import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2Icon } from "lucide-react";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-[none] focus-visible:border-neutral-600 focus-visible:ring-neutral-600/50 focus-visible:ring-[3px] aria-invalid:ring-danger/20 dark:aria-invalid:ring-danger/40 aria-invalid:border-danger",
	{
		variants: {
			weight: {
				normal: "font-normal",
				medium: "font-medium",
			},
			variant: {
				default:
					"bg-neutral-900 text-neutral-50 hover:bg-neutral-900/90 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-50/90",
				destructive:
					"bg-danger-500 text-neutral-50 hover:bg-danger-500/90 dark:bg-danger-900 dark:text-neutral-50 dark:hover:bg-danger-900/90",
				outline:
					"border border-input text-outline-foreground bg-outline-button hover:bg-accent hover:text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-50",
				"outline-primary":
					"border border-primary bg-transparent text-primary hover:bg-neutral-100",
				// An applied filter. Looks like outline-primary until a theme sets the
				// filter-active tokens (admin-app's dark theme gives it a muted fill).
				"filter-active":
					"border border-filter-active-border bg-filter-active text-filter-active-foreground hover:bg-neutral-100",
				"outline-investor-portal":
					"border border-primary bg-transparent text-primary hover:bg-primary hover:text-white",
				"outline-danger":
					"border border-danger bg-transparent text-danger hover:bg-neutral-100",
				"outline-success":
					"border border-success bg-transparent text-success hover:bg-neutral-100",
				secondary:
					"bg-muted text-neutral-900 hover:bg-muted/80 dark:bg-neutral-800 dark:text-neutral-50 dark:hover:bg-neutral-800/80",
				ghost:
					"text-neutral-900 hover:bg-accent hover:text-neutral-900 dark:text-neutral-50 dark:hover:bg-neutral-800 dark:hover:text-neutral-50",
				link: "text-neutral-900 underline-offset-4 hover:underline dark:text-neutral-50",
				"outline-muted-primary":
					"border border-cold-gray-200 bg-transparent text-cold-gray-50 hover:border-primary-400 hover:bg-primary-400 hover:text-white",
				"inverted-secondary":
					"border border-cold-gray-200 bg-transparent text-cold-gray-50 hover:border-white hover:bg-white hover:text-neutral-800",
				primary:
					"bg-primary-400 text-cold-gray-50 hover:bg-primary-button-hover disabled:!bg-cold-gray-300",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-8 rounded-[4px] px-3",
				lg: "h-11 rounded-[4px] px-8",
				icon: "h-10 w-10",
				"icon-sm": "h-8 w-8",
				"icon-xs": "h-6 w-6",
			},
		},
		defaultVariants: {
			variant: "outline",
			size: "sm",
			weight: "normal",
		},
	},
);

/**
 * Props for the {@link Button} component.
 *
 * @property asChild - When `true`, renders the child element directly via Radix `Slot` instead of a `<button>`.
 * @property loading - When `true`, disables the button and shows a spinner.
 */
export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
		loading?: boolean;
	};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			weight,
			asChild = false,
			loading,
			disabled,
			children,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : "button";

		const isDisabled = loading || disabled;
		const isAsChildDisabled = asChild && isDisabled;

		return (
			<Comp
				className={cn(
					buttonVariants({ variant, size, weight, className }),
					isAsChildDisabled && "pointer-events-none opacity-50",
				)}
				ref={ref}
				type={!asChild ? "button" : undefined}
				disabled={!asChild ? isDisabled : undefined}
				aria-disabled={isAsChildDisabled || undefined}
				aria-busy={loading || undefined}
				{...props}
			>
				{asChild ? (
					children
				) : (
					<>
						{loading && <Loader2Icon className="animate-spin size-4" />}
						{children}
					</>
				)}
			</Comp>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
